import { forwardRef, useEffect, useRef } from 'react';
import { Select as AntdSelect } from 'antd';
import './Select.module.css';

const SIZE_STYLE = { minHeight: 44, fontSize: 16, alignItems: 'center' };
const NAV_KEYS = ['ArrowDown', 'ArrowUp'];
const FOCUSED_CLASS = 'vec-select-option-focused';

/**
 * @param {Object} props - Same shape as antd's own `Select`.
 * @param {Object} [props.styles] - `styles.root` merges with a 44px-tall, 16px-font sizing
 *   baseline; pass your own to override just that key.
 * @param {Function} [props.getPopupContainer] - Where the dropdown popup mounts; defaults to the
 *   field's own parent element. Still called if you pass your own.
 * @param {Function} [props.onOpenChange] - Called on every open/close, alongside the keyboard-vs-
 *   mouse tracking that drives the dropdown's focus ring.
 */
const Select = forwardRef(({ styles, onOpenChange, getPopupContainer, ...props }, ref) => {
    const containerRef = useRef(null);
    const cleanupRef = useRef(null);

    const resolvePopupContainer = (trigger) => {
        const container = getPopupContainer ? getPopupContainer(trigger) : trigger.parentElement;
        containerRef.current = container;
        return container;
    };

    const handleOpenChange = (nextOpen) => {
        cleanupRef.current?.();
        cleanupRef.current = null;

        if (nextOpen) {
            let isKeyboard = false;

            const applyFocusedClass = () => {
                const dropdown = containerRef.current?.querySelector('.ant-select-dropdown');
                if (!dropdown) return;

                observer.disconnect();
                dropdown.querySelectorAll(`.${FOCUSED_CLASS}`).forEach((el) => el.classList.remove(FOCUSED_CLASS));
                if (isKeyboard) {
                    dropdown.querySelector('.ant-select-item-option-active')?.classList.add(FOCUSED_CLASS);
                }
                observer.observe(dropdown, { attributes: true, attributeFilter: ['class'], subtree: true });
            };

            const handleKeyDown = (event) => {
                if (NAV_KEYS.includes(event.key)) {
                    isKeyboard = true;
                    applyFocusedClass();
                }
            };

            const handleMouseMove = () => {
                if (isKeyboard) {
                    isKeyboard = false;
                    applyFocusedClass();
                }
            };

            const observer = new MutationObserver(applyFocusedClass);
            const raf = requestAnimationFrame(() => {
                const dropdown = containerRef.current?.querySelector('.ant-select-dropdown');
                dropdown && observer.observe(dropdown, { attributes: true, attributeFilter: ['class'], subtree: true });
            });

            document.addEventListener('keydown', handleKeyDown, true);
            containerRef.current?.addEventListener('mousemove', handleMouseMove, true);

            cleanupRef.current = () => {
                cancelAnimationFrame(raf);
                observer.disconnect();
                document.removeEventListener('keydown', handleKeyDown, true);
                containerRef.current?.removeEventListener('mousemove', handleMouseMove, true);
            };
        }

        onOpenChange?.(nextOpen);
    };

    useEffect(() => () => cleanupRef.current?.(), []);

    return (
        <AntdSelect
            ref={ref}
            styles={{ ...styles, root: { ...SIZE_STYLE, ...styles?.root } }}
            getPopupContainer={resolvePopupContainer}
            onOpenChange={handleOpenChange}
            {...props}
        />
    );
});
Select.displayName = 'Select';

Select.Option = AntdSelect.Option;
Select.OptGroup = AntdSelect.OptGroup;

export default Select;
